import { ReactElement, useCallback } from "react";
import {
	Icon,
	Spinner,
	TreeAccordion
} from "client/Components";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import { showIncorrectWebDavCredentialsDialog } from "client_legacy/Documents/WebDav/Dialogs/incorrectCredentialsDialog";
import styles from "./SideBarMenu.module.less";
import { ServerLoadingState } from "./WebDavPickerStore";
import { EnabledServer } from "./ItemSelector";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import {
	LinkKind,
	useLinkToIntegrationCredentialsDialog
} from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import { WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

const SIDE_BAR_ID = "item-picker-webdav-sidebar";

export type SideBarMenuProps = {
	serverLoadingState: ServerLoadingState;
	currentServerId?: number;
	onSelectServer(server: WebDavServerModel): void;
	onReloadSideBar(): void;
	enabledServer?: EnabledServer;

};

export const SideBarMenu = (props: SideBarMenuProps): ReactElement => {
	const {
		serverLoadingState,
		currentServerId,
		onSelectServer,
		onReloadSideBar,
		enabledServer
	} = props;

	const { isExpanded, toggleExpansion } = useToggleExpansion([SIDE_BAR_ID]);
	const [linkToIntegrationCredentialsDialog, openLinkToIntegrationCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	const clickHandler = async (server?: WebDavServerModel): Promise<void> => {
		const { integrationCredentialsConfiguredLinks } = userConfiguration();
		if (!integrationCredentialsConfiguredLinks.fileServers) {
			const result: TryResult<void> = await openLinkToIntegrationCredentialsDialog({
				thingToLink: {
					kind: LinkKind.FileServers
				}
			});

			if (!result.wasSuccessful) {
				return;
			}

			onReloadSideBar();

			if (server) {
				onSelectServer(server);
			}
		} else {
			if (await offerPasswordUpdateWhenPossibleChanged({ kind: IntegrationCredentialsLinkKind.FileServers })) {
				onReloadSideBar();
			} else if (!server) {
				showIncorrectWebDavCredentialsDialog();
			}
			if (server) {
				onSelectServer(server);
			}
		}
	};

	const isServerDisabled: (id: number) => boolean = useCallback(
		(id: number): boolean => {
			if(enabledServer === undefined || enabledServer.kind === "all" ){
				return false;
			}

			if(enabledServer.kind === "none"){
				return true;
			}

			return enabledServer.serverId !== id;
		},
		[enabledServer]
	);

	const toggleExpansionHandler = useCallback(
		(): void => toggleExpansion(SIDE_BAR_ID),
		[toggleExpansion]
	);

	return (
		<>
			<TreeAccordion trackingId={SIDE_BAR_ID}>
				<TreeAccordion.Node>
					<TreeAccordion.Item
						{...(
							serverLoadingState.kind === "success"
								? {
									expansionToggle: "visible",
									isExpanded: isExpanded(SIDE_BAR_ID),
									onToggleExpansion: toggleExpansionHandler
								}
								: {
									expansionToggle: "hidden"
								}
						)}
						trackingId={generateTrackingId(SIDE_BAR_ID,"__header")}
						icon="server"
						onClick={serverLoadingState.kind === "error"
							? clickHandler
							: toggleExpansionHandler
						}
					>
						<div className={styles.itemContent}>
							<div className={styles.itemContent__text}>{localize("_WebDavStoreTitle")}</div>
							{serverLoadingState.kind === "loading" && <Spinner isInline/>}
							{serverLoadingState.kind === "error"
								&& (
									<Icon value={{
										kind: "font",
										name: "triangle-exclamation",
										color: "danger"
									}}/>
								)
							}
						</div>
					</TreeAccordion.Item>
					{serverLoadingState.kind === "success" && (
						<TreeAccordion.SubTree
							isCollapsed={!isExpanded(SIDE_BAR_ID)}
						>
							{serverLoadingState.servers
								.map(
									(x: WebDavServerModel) => {
										const isDisabled: boolean = isServerDisabled(x.id);
										return (
											<TreeAccordion.Node key={x.id}>
												<TreeAccordion.Item
													isSelected={currentServerId === x.id}
													onClick={() => clickHandler(x)}
													trackingId={generateTrackingId(SIDE_BAR_ID, `|server-${x.id}`)}
													indentionLevel={1}
													isDisabled={isDisabled}
													title={isDisabled ? enabledServer?.reason : undefined}
													expansionToggle="hidden"
												>
													{x.name}
												</TreeAccordion.Item>
											</TreeAccordion.Node>
										);
									}
								)
						}
						</TreeAccordion.SubTree>
					)}
				</TreeAccordion.Node>
			</TreeAccordion>
			{linkToIntegrationCredentialsDialog}
		</>
	);
};

SideBarMenu.displayName = "SideBarMenu";